import { useEffect, useRef } from 'react';

/**
 * After a gap in the live connection (socket reconnected, tab visible again
 * after HIDDEN_RESYNC_MS, browser back `online`; a disconnected socket resyncs
 * on its reconnect instead of on the trigger) whatever was pushed in the
 * meantime is lost, so SocketProvider broadcasts a resync and the loaders
 * re-read: the inbox's first page (ConversationContext), the open
 * conversation's newest page (Converse), the member's wallet and unread count
 * (UserSocketManager). Each merges by id, so nothing is duplicated or dropped.
 */
export const RESYNC_EVENT = 'app:resync';

/** A tab hidden at least this long resyncs when it becomes visible again. */
export const HIDDEN_RESYNC_MS = 30000;

/**
 * `online` / a visible tab with the socket down waits this long for its
 * reconnect to resync (one more than the Manager's longest delay, 10 s), then
 * resyncs itself.
 */
export const RECONNECT_RESYNC_GRACE_MS = 11000;

/** Triggers arriving together (a visible tab and `online` at once) resync once. */
const RESYNC_THROTTLE_MS = 3000;

export type ResyncReason = 'reconnect' | 'visible' | 'online';

let lastResync = 0;

export const requestResync = (reason: ResyncReason): void => {
  const now = Date.now();
  if (now - lastResync < RESYNC_THROTTLE_MS) {
    return;
  }
  lastResync = now;
  console.debug('Resync after', reason);
  window.dispatchEvent(new CustomEvent(RESYNC_EVENT, { detail: { reason } }));
};

/** Runs `handler` on every resync; the latest handler is used, so it need not be memoised. */
export function useResync(handler: () => void): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    const onResync = () => handlerRef.current();
    window.addEventListener(RESYNC_EVENT, onResync);
    return () => window.removeEventListener(RESYNC_EVENT, onResync);
  }, []);
}
